import * as React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider, createTheme, useColorScheme } from '@mui/material/styles';

import { inputsCustomizations } from './customizations/inputs';
import { dataDisplayCustomizations } from './customizations/dataDisplay';
import { feedbackCustomizations } from './customizations/feedback';
import { navigationCustomizations } from './customizations/navigation';
import { surfacesCustomizations } from './customizations/surfaces';
import { colorSchemes, devColorScheme, typography, shadows, shape } from './themePrimitives';

// Create a context for dev mode
export const DevModeContext = React.createContext({
  isDevMode: false,
  setIsDevMode: () => {},
});

export function useDevMode() {
  return React.useContext(DevModeContext);
}

function AppTheme({ children, disableCustomTheme, themeComponents }) {
  const [isDevMode, setIsDevMode] = React.useState(() => {
    return localStorage.getItem('devMode') === 'true';
  });

  React.useEffect(() => {
    localStorage.setItem('devMode', isDevMode.toString());
  }, [isDevMode]);

  const theme = React.useMemo(() => {
    if (disableCustomTheme) {
      return {};
    }

    const baseComponents = {
      ...inputsCustomizations,
      ...dataDisplayCustomizations,
      ...feedbackCustomizations,
      ...navigationCustomizations,
      ...surfacesCustomizations,
      ...themeComponents,
    };

    if (isDevMode) {
      // Create dev theme with CSS variables, but using dev color scheme
      return createTheme({
        cssVariables: {
          colorSchemeSelector: 'data-mui-color-scheme',
          cssVarPrefix: 'template',
        },
        colorSchemes: {
          light: devColorScheme,
          dark: devColorScheme, // Use dev colors for both modes
        },
        typography,
        shadows,
        shape,
        components: baseComponents,
      });
    }

    // Create normal theme with CSS variables for light/dark mode
    return createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      colorSchemes,
      typography,
      shadows,
      shape,
      components: baseComponents,
    });
  }, [disableCustomTheme, themeComponents, isDevMode]);

  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <DevModeContext.Provider value={{ isDevMode, setIsDevMode }}>
      <ThemeProvider theme={theme} disableTransitionOnChange>
        {children}
      </ThemeProvider>
    </DevModeContext.Provider>
  );
}

AppTheme.propTypes = {
  children: PropTypes.node,
  /**
   * This is for the docs site. You can ignore it or remove it.
   */
  disableCustomTheme: PropTypes.bool,
  themeComponents: PropTypes.object,
};

export default AppTheme;
